/** Parse custom XML tags from AI assistant responses. */
export function parseCustomTags(
  customTag: string,
  content: string
): { customTagContent: string | null; response: string } {
  const customTagRegex = new RegExp(`<${customTag}>([\\s\\S]*?)<\\/${customTag}>`, 'gi');
  let customTagContent: string | null = null;
  const customTagMatches = content.match(customTagRegex);
  if (customTagMatches) {
    customTagContent = customTagMatches
      .map((m) => m.replace(`<${customTag}>`, '').replace(`</${customTag}>`, '').trim())
      .filter(Boolean)
      .join('\n\n');
  }
  const response = content.replace(customTagRegex, '').trim();
  return { customTagContent, response };
}

export function parseJsonArrayFromTagContent(customTagContent: string | null): unknown[] {
  if (!customTagContent || customTagContent.trim().length === 0) {
    return [];
  }

  try {
    const parsed = JSON.parse(customTagContent) as unknown;
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [customTagContent.trim()];
  }
}

export interface ParsedAssistantResponse {
  thinking: string | null;
  jobs: unknown[];
  payloads: unknown[];
  actions: unknown[];
  response: string;
}

/** Structured blocks embedded in assistant markdown (think, jobs, payloads, actions). */
export function parseAssistantResponse(content: string): ParsedAssistantResponse {
  const thinkParsed = parseCustomTags('think', content);
  const thinkingParsed = parseCustomTags('thinking', thinkParsed.response);
  const jobParsed = parseCustomTags('job', thinkingParsed.response);
  const jobsParsed = parseCustomTags('jobs', jobParsed.response);
  const actionParsed = parseCustomTags('action', jobsParsed.response);
  const actionsParsed = parseCustomTags('actions', actionParsed.response);
  const payloadParsed = parseCustomTags('payload', actionsParsed.response);
  const payloadsParsed = parseCustomTags('payloads', payloadParsed.response);

  const thinking = [thinkParsed.customTagContent, thinkingParsed.customTagContent]
    .filter((part): part is string => Boolean(part && part.trim().length > 0))
    .join('\n\n')
    .trim();

  const jobsRaw = [jobParsed.customTagContent, jobsParsed.customTagContent]
    .filter((part): part is string => Boolean(part && part.trim().length > 0))
    .join('\n\n')
    .trim();

  const actionsRaw = [actionParsed.customTagContent, actionsParsed.customTagContent]
    .filter((part): part is string => Boolean(part && part.trim().length > 0))
    .join('\n\n')
    .trim();

  const payloadsRaw = [payloadParsed.customTagContent, payloadsParsed.customTagContent]
    .filter((part): part is string => Boolean(part && part.trim().length > 0))
    .join('\n\n')
    .trim();

  return {
    thinking: thinking.length > 0 ? thinking : null,
    jobs: parseJsonArrayFromTagContent(jobsRaw || null),
    actions: parseJsonArrayFromTagContent(actionsRaw || null),
    payloads: parseJsonArrayFromTagContent(payloadsRaw || null),
    response: payloadsParsed.response,
  };
}
